// src/pages/PricingPage.js
import React, { useState } from 'react';
import { Box, Typography, Button, List, ListItem, ListItemIcon, ListItemText, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'framer-motion';
import useSEO from '../hooks/useSEO';
import Breadcrumbs from '../components/common/Breadcrumbs';

const BREADCRUMBS = [{ name: 'Pricing', url: 'https://dublabs.ai/pricing' }];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const PLANS = [
  {
    name: 'Creator',
    tagline: 'For independent creators',
    description: 'YouTubers, influencers, and solo creators expanding their global reach.',
    volume: '1–50 videos / month',
    features: ['Up to 30 min per video', '20+ languages', 'Perfect lip-sync', 'HD output', 'Email support', '48h turnaround'],
    cta: 'Get Creator Pricing',
    featured: false,
  },
  {
    name: 'Agency Pro',
    tagline: 'Most popular',
    description: 'Marketing agencies, production houses, and growing content businesses.',
    volume: '50–500 videos / month',
    features: ['Up to 2 hours per video', '25+ languages', 'Advanced lip-sync', '4K output', 'Priority support', '24h turnaround', 'White-label options', 'API access', 'Custom voice training'],
    cta: 'Contact Sales',
    featured: true,
  },
  {
    name: 'Enterprise',
    tagline: 'For large media houses',
    description: 'Broadcasters, studios, and large-scale content operations worldwide.',
    volume: '500+ videos / month',
    features: ['Unlimited video length', 'All languages', 'Premium lip-sync', '8K output', 'Dedicated account manager', '12h turnaround', 'Full white-label', 'Custom API', 'SLA guarantee'],
    cta: 'Schedule Demo',
    featured: false,
  },
];

const FAQS = [
  { q: 'How is pricing calculated?', a: 'Pricing is based on video length, target languages, quality requirements, volume commitments, and turnaround time. Every plan is customized to your needs.' },
  { q: 'Do you offer volume discounts?', a: 'Yes. The more content you process, the better your per-minute rates. Contact us for a custom quote based on your expected monthly volume.' },
  { q: 'Is there a minimum commitment?', a: 'Creator plans have no minimum. Agency and Enterprise plans include monthly minimums that unlock better pricing and priority processing.' },
  { q: 'Can I see a demo before committing?', a: 'Yes — we run personalized demos scoped to your content, target languages, and quality bar. Book one and we\'ll show you real dubbed output before you sign anything.' },
  { q: 'How long does processing take?', a: 'Creator: 48 hours. Agency Pro: 24 hours. Enterprise: 12 hours. Rush processing available on request.' },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  useSEO({
    title: 'DubLabs Pricing — Custom AI Video Dubbing Solutions',
    description: 'Flexible per-video pricing for AI video dubbing. Custom plans for creators, marketing agencies, and enterprise media teams — book a demo to get yours.',
    canonical: 'https://dublabs.ai/pricing',
    breadcrumbs: BREADCRUMBS,
    faqs: FAQS.map((f) => ({ question: f.q, answer: f.a })),
    dateModified: '2026-07-30',
  });

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', py: { xs: 8, md: 14 }, px: { xs: 2, md: 4 } }}>
      <Breadcrumbs items={BREADCRUMBS} />
      <Box sx={{ maxWidth: '1100px', mx: 'auto' }}>

        {/* Header */}
        <Box
          component={motion.div}
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={fadeUp}
          sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 2 }}>
            Pricing
          </Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.6rem' }, fontWeight: 800, letterSpacing: '-0.035em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 2 }}>
            Priced for every scale
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1.1rem', fontFamily: 'Inter, sans-serif', maxWidth: '520px', mx: 'auto', lineHeight: 1.65 }}>
            From solo creators to major broadcasters. All plans are custom-tailored — contact us for your quote.
          </Typography>
        </Box>

        {/* Pricing cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
            mb: { xs: 10, md: 16 },
            alignItems: 'stretch',
          }}
        >
          {PLANS.map((plan, i) => (
            <Box
              key={plan.name}
              component={motion.div}
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              custom={i} variants={fadeUp}
              sx={{
                position: 'relative',
                borderRadius: '16px',
                backgroundColor: plan.featured ? '#0E0E0E' : '#0A0A0A',
                border: plan.featured ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(255,255,255,0.06)',
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                ...(plan.featured && {
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: '15%', right: '15%',
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                  },
                }),
              }}
            >
              {plan.featured && (
                <Box sx={{
                  position: 'absolute', top: 16, right: 16,
                  px: 1.5, py: 0.4, borderRadius: '6px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}>
                  <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.04em' }}>
                    Most Popular
                  </Typography>
                </Box>
              )}

              <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '20px', fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em', mb: 0.5 }}>
                {plan.name}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px', fontWeight: 500, fontFamily: 'Inter, sans-serif', mb: 2.5 }}>
                {plan.tagline}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', fontFamily: 'Inter, sans-serif', lineHeight: 1.65, mb: 3 }}>
                {plan.description}
              </Typography>

              <Box sx={{ px: 1.5, py: 0.75, borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)', display: 'inline-block', mb: 3, alignSelf: 'flex-start' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '12px', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                  {plan.volume}
                </Typography>
              </Box>

              <List sx={{ flex: 1, p: 0, mb: 4 }}>
                {plan.features.map((f) => (
                  <ListItem key={f} sx={{ px: 0, py: 0.6 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircleIcon sx={{ fontSize: 15, color: '#22C55E' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={f}
                      primaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.55)', fontSize: '13.5px', fontFamily: 'Inter, sans-serif' } }}
                    />
                  </ListItem>
                ))}
              </List>

              <Button
                component={motion.button}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/contact')}
                fullWidth
                sx={{
                  py: 1.4,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '14px',
                  ...(plan.featured
                    ? { backgroundColor: '#FFFFFF', color: '#000000', '&:hover': { backgroundColor: '#F0F0F0' } }
                    : { border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF', backgroundColor: 'transparent', '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.22)' } }
                  ),
                }}
              >
                {plan.cta}
              </Button>
            </Box>
          ))}
        </Box>

        {/* FAQ */}
        <Box
          component={motion.div}
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={fadeUp}
          sx={{ mb: { xs: 10, md: 16 } }}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 3, textAlign: 'center' }}>
            FAQ
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, letterSpacing: '-0.03em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 6, textAlign: 'center' }}>
            Common questions
          </Typography>

          {FAQS.map((faq, i) => (
            <Accordion
              key={i}
              expanded={expanded === i}
              onChange={(_, open) => setExpanded(open ? i : false)}
              sx={{
                backgroundColor: '#0A0A0A',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '10px !important',
                mb: 1.5,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { borderColor: 'rgba(255,255,255,0.12)' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />}>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '15px', fontFamily: 'Inter, sans-serif', letterSpacing: '-0.01em' }}>
                  {faq.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', fontFamily: 'Inter, sans-serif', lineHeight: 1.75 }}>
                  {faq.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Bottom CTA */}
        <Box
          component={motion.div}
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={fadeUp}
          sx={{ textAlign: 'center', p: { xs: 5, md: 8 }, borderRadius: '20px', backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.07)', position: 'relative', overflow: 'hidden',
            '&::before': { content: '""', position: 'absolute', top: 0, left: '25%', right: '25%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)' },
          }}
        >
          <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, letterSpacing: '-0.03em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 2 }}>
            Ready to go global?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem', fontFamily: 'Inter, sans-serif', mb: 5, maxWidth: '400px', mx: 'auto', lineHeight: 1.65 }}>
            Contact our team for a personalized quote and live demo tailored to your content volume.
          </Typography>
          <Button
            component={motion.button}
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/contact')}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
            sx={{ backgroundColor: '#FFFFFF', color: '#000000', fontWeight: 700, fontSize: '15px', px: 4, py: 1.5, borderRadius: '8px', textTransform: 'none', fontFamily: 'Inter, sans-serif', '&:hover': { backgroundColor: '#F0F0F0' } }}
          >
            Contact Sales
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
